import React from "react";
import { Container, Row, Col, Card, Badge } from "react-bootstrap";
import { motion } from "framer-motion";
import { accountTypes } from "../../../utils/data";
import {
  FaRobot,
  FaChartLine,
  FaHeadset,
  FaCrown,
  FaCog,
  FaUniversity,
  FaBitcoin,
  FaGem,
  FaStar,
} from "react-icons/fa";

// Tier icons mapping
const tierIcons = {
  Bronze: <FaStar className="tier-icon" />,
  Silver: <FaStar className="tier-icon" />,
  Gold: <FaCrown className="tier-icon" />,
  Platinum: <FaCrown className="tier-icon" />,
  Diamond: <FaGem className="tier-icon" />,
  Elite: <FaGem className="tier-icon" />,
};

const FeatureCategory = ({ icon, title, features }) => (
  <div className="feature-category d-flex flex-column mb-4 text-center">
    <div className="d-flex align-items-center justify-content-center mb-3">
      {icon}
      <h5 className="mb-0 ms-2">{title}</h5>
    </div>
    <ul className="features-list ps-0 d-flex flex-column align-items-center">
      {features.map((feature, idx) => (
        <li key={idx} className="mb-2 d-flex align-items-center">
          <i className="fas fa-check-circle text-success me-2"></i>
          <span>{feature}</span>
        </li>
      ))}
    </ul>
  </div>
);

const CompactFeatureList = ({ features, maxItems = 5 }) => (
  <ul className="features-list ps-0 mb-0">
    {features.slice(0, maxItems).map((feature, idx) => (
      <li key={idx} className="mb-1 d-flex align-items-start small">
        <i className="fas fa-check-circle text-success me-2 mt-1" style={{ fontSize: '0.7rem' }}></i>
        <span>{feature}</span>
      </li>
    ))}
  </ul>
);

const AccountTypes = () => {
  // Split tiers into rows for better display
  const retailTiers = accountTypes[0].tiers;
  const firstRowTiers = retailTiers.slice(0, 3); // Bronze, Silver, Gold
  const secondRowTiers = retailTiers.slice(3, 6); // Platinum, Diamond, Elite

  return (
    <section className="account-types-section pb-5 pt-4">
      <Container>
        <Row className="justify-content-center mb-5">
          <Col md={10} lg={8}>
            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="fw-bold mb-3">Choose Your Trading Account</h2>
              <p className="text-muted">
                Select from 7 account tiers designed to match your trading needs and
                investment goals. Each tier includes our proven trading bot
                and CTE benefits, with progressively enhanced AI assistance,
                exclusive features, and higher returns.
              </p>
            </motion.div>
          </Col>
        </Row>

        {/* Retail Account Tiers - First Row (Bronze, Silver, Gold) */}
        <Row className="mb-4">
          <Col>
            <h3 className="text-center mb-4">
              <FaStar className="text-warning me-2" />
              Starter Tiers
            </h3>
            <Row className="g-4 justify-content-center">
              {firstRowTiers.map((tier, index) => (
                <Col key={tier.name} sm={12} md={6} lg={4}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="h-100"
                  >
                    <Card
                      className={`h-100 account-card ${tier.popular ? 'popular-tier' : ''}`}
                      style={{
                        borderTop: `4px solid ${tier.tierColor}`,
                      }}
                    >
                      {tier.popular && (
                        <div className="popular-badge">
                          <Badge bg="warning" text="dark" className="position-absolute top-0 start-50 translate-middle px-3 py-2">
                            Most Popular
                          </Badge>
                        </div>
                      )}
                      <Card.Body className="d-flex flex-column">
                        <div className="text-center mb-3">
                          <div
                            className="tier-icon-wrapper mb-2 mx-auto d-flex align-items-center justify-content-center"
                            style={{
                              color: tier.tierColor,
                              width: '50px',
                              height: '50px',
                              borderRadius: '50%',
                              backgroundColor: `${tier.tierColor}20`,
                            }}
                          >
                            {tierIcons[tier.name]}
                          </div>
                          <h4 className="mb-1" style={{ color: tier.tierColor }}>{tier.name}</h4>
                          <div className="min-deposit">
                            <span className="text-muted small">Minimum Deposit</span>
                            <h3 className="mb-0 fw-bold">{tier.minDeposit}</h3>
                          </div>
                          {tier.cteBonus && (
                            <div className="cte-bonus mt-2">
                              <Badge
                                className="py-2 px-3"
                                style={{ backgroundColor: '#00e7d4', color: '#000' }}
                              >
                                📈 {tier.cteBonus} CTE Bonus
                              </Badge>
                            </div>
                          )}
                        </div>

                        <hr className="my-3" />

                        <div className="flex-grow-1">
                          <h6 className="text-primary mb-2">
                            <FaRobot className="me-2" />
                            AI Features
                          </h6>
                          <CompactFeatureList features={tier.aiFeatures} maxItems={3} />

                          <h6 className="text-success mb-2 mt-3">
                            <FaChartLine className="me-2" />
                            Trading Features
                          </h6>
                          <CompactFeatureList
                            features={tier.features.filter(f => !f.includes("support"))}
                            maxItems={5}
                          />
                        </div>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </Col>
        </Row>

        {/* Retail Account Tiers - Second Row (Platinum, Diamond, Elite) */}
        <Row className="mb-5">
          <Col>
            <h3 className="text-center mb-4 mt-5">
              <FaCrown className="text-warning me-2" />
              Premium Tiers
            </h3>
            <Row className="g-4 justify-content-center">
              {secondRowTiers.map((tier, index) => (
                <Col key={tier.name} sm={12} md={6} lg={4}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="h-100"
                  >
                    <Card
                      className="h-100 account-card premium-card"
                      style={{
                        borderTop: `4px solid ${tier.tierColor}`,
                        background: 'linear-gradient(135deg, rgba(26, 26, 46, 0.95) 0%, rgba(22, 33, 62, 0.95) 100%)',
                      }}
                    >
                      <Card.Body className="d-flex flex-column">
                        <div className="text-center mb-3">
                          <div
                            className="tier-icon-wrapper mb-2 mx-auto d-flex align-items-center justify-content-center"
                            style={{
                              color: tier.tierColor,
                              width: '60px',
                              height: '60px',
                              borderRadius: '50%',
                              backgroundColor: `${tier.tierColor}30`,
                              boxShadow: `0 0 20px ${tier.tierColor}40`,
                            }}
                          >
                            {tierIcons[tier.name]}
                          </div>
                          <h4 className="mb-1" style={{ color: tier.tierColor }}>{tier.name}</h4>
                          <div className="min-deposit">
                            <span className="text-muted small">Minimum Deposit</span>
                            <h3 className="mb-0 fw-bold text-white">{tier.minDeposit}</h3>
                          </div>
                          {tier.cteBonus && (
                            <div className="cte-bonus mt-2">
                              <Badge
                                className="py-2 px-3"
                                style={{ backgroundColor: '#00e7d4', color: '#000' }}
                              >
                                📈 {tier.cteBonus} CTE Bonus
                              </Badge>
                            </div>
                          )}
                        </div>

                        <hr className="my-3" style={{ borderColor: 'rgba(255,255,255,0.2)' }} />

                        <div className="flex-grow-1">
                          <h6 className="mb-2" style={{ color: tier.tierColor }}>
                            <FaRobot className="me-2" />
                            AI Features
                          </h6>
                          <CompactFeatureList features={tier.aiFeatures} maxItems={4} />

                          <h6 className="mb-2 mt-3" style={{ color: tier.tierColor }}>
                            <FaChartLine className="me-2" />
                            Premium Features
                          </h6>
                          <CompactFeatureList
                            features={tier.features.filter(f => !f.includes("support"))}
                            maxItems={6}
                          />
                        </div>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </Col>
        </Row>

        {/* Institutional Account */}
        <Row className="mb-5">
          <Col md={12} lg={10} className="mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <Card className="institutional-card" style={{
                background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                border: '2px solid #00e7d4',
              }}>
                <Card.Body className="p-4 p-lg-5">
                  <div className="text-center mb-4">
                    <div
                      className="tier-icon-wrapper mb-3 mx-auto d-flex align-items-center justify-content-center"
                      style={{
                        color: '#00e7d4',
                        width: '80px',
                        height: '80px',
                        borderRadius: '50%',
                        backgroundColor: 'rgba(0, 231, 212, 0.2)',
                        boxShadow: '0 0 30px rgba(0, 231, 212, 0.3)',
                      }}
                    >
                      <FaUniversity size={36} />
                    </div>
                    <Badge style={{ backgroundColor: '#00e7d4', color: '#000' }} className="mb-2 px-3 py-2">Enterprise Grade</Badge>
                    <h2 className="text-white">{accountTypes[1].type}</h2>
                    <div className="min-deposit">
                      <span className="text-muted">Minimum Deposit</span>
                      <h2 className="mb-0 text-white fw-bold">{accountTypes[1].minDeposit}</h2>
                    </div>
                    <div className="d-flex justify-content-center gap-3 mt-3">
                      <Badge
                        className="py-2 px-3"
                        style={{ backgroundColor: '#00e7d4', color: '#000' }}
                      >
                        📈 {accountTypes[1].cteBonus} CTE Bonus
                      </Badge>
                    </div>
                    <p className="text-muted mt-3 mb-0">
                      For hedge funds, family offices, and high-net-worth individuals seeking
                      institutional-grade trading infrastructure and maximum returns.
                    </p>
                  </div>

                  <Row className="mt-4">
                    <Col lg={4} md={6} className="mb-4">
                      <FeatureCategory
                        icon={<FaRobot className="text-primary" size={24} />}
                        title="Enterprise AI"
                        features={accountTypes[1].aiFeatures.slice(0, 5)}
                      />
                    </Col>
                    <Col lg={4} md={6} className="mb-4">
                      <FeatureCategory
                        icon={<FaCog className="text-info" size={24} />}
                        title="Institutional Features"
                        features={accountTypes[1].features.filter(f => !f.includes("AI")).slice(0, 8)}
                      />
                    </Col>
                    <Col lg={4} md={12} className="mb-4">
                      <FeatureCategory
                        icon={<FaHeadset className="text-success" size={24} />}
                        title="Exclusive Benefits"
                        features={accountTypes[1].additionalBenefits}
                      />
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </motion.div>
          </Col>
        </Row>

        {/* Payout Methods Section */}
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <Card className="payout-methods-card">
                <Card.Body className="text-center">
                  <h3 className="mb-4">Flexible Payout Methods</h3>
                  <p className="text-muted mb-4">
                    Choose from secure and convenient payout options that suit
                    your preferences
                  </p>

                  <Row className="g-4">
                    <Col md={6}>
                      <div className="payout-method p-4 rounded-3 h-100 d-flex flex-column justify-content-center align-items-center text-center">
                        <div className="payout-icon mb-3">
                          <FaUniversity size={48} className="text-primary" />
                        </div>
                        <h5 className="mb-3">Bank Wire Transfer</h5>
                        <ul className="features-list ps-0">
                          <li className="mb-2 d-flex align-items-center">
                            <i className="fas fa-check-circle text-success me-2"></i>
                            <span>Secure international transfers</span>
                          </li>
                          <li className="mb-2 d-flex align-items-center">
                            <i className="fas fa-check-circle text-success me-2"></i>
                            <span>Direct to your bank account</span>
                          </li>
                          <li className="mb-2 d-flex align-items-center">
                            <i className="fas fa-check-circle text-success me-2"></i>
                            <span>SWIFT network support</span>
                          </li>
                          <li className="mb-2 d-flex align-items-center">
                            <i className="fas fa-check-circle text-success me-2"></i>
                            <span>Multi-currency support</span>
                          </li>
                        </ul>
                      </div>
                    </Col>

                    <Col md={6}>
                      <div className="payout-method p-4 rounded-3 h-100 d-flex flex-column justify-content-center align-items-center text-center">
                        <div className="payout-icon mb-3">
                          <FaBitcoin size={48} className="text-warning" />
                        </div>
                        <h5 className="mb-3">Crypto Transfer</h5>
                        <ul className="features-list ps-0 text-start">
                          <li className="mb-2 d-flex align-items-center">
                            <i className="fas fa-check-circle text-success me-2"></i>
                            <span>Bitcoin (BTC) transfers</span>
                          </li>
                          <li className="mb-2 d-flex align-items-center">
                            <i className="fas fa-check-circle text-success me-2"></i>
                            <span>Ethereum (ETH) transfers</span>
                          </li>
                          <li className="mb-2 d-flex align-items-center">
                            <i className="fas fa-check-circle text-success me-2"></i>
                            <span>Fast blockchain settlement</span>
                          </li>
                          <li className="mb-2 d-flex align-items-center">
                            <i className="fas fa-check-circle text-success me-2"></i>
                            <span>Low transaction fees</span>
                          </li>
                        </ul>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </motion.div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default AccountTypes;
